import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext();

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function formatApiErrorDetail(detail) {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).filter(Boolean).join(' ');
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
}

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// Restoring the session on page load is NOT the same as "is this request
// authorised?", so it cannot be left to the api.js 401 interceptor — that one
// deliberately skips /auth/ calls, and this call is made on bare axios anyway.
//
// Two failures used to be collapsed into "logged out", and both bounced a
// perfectly valid session to the login screen:
//
//  1. The access_token cookie lives 15 minutes; the refresh_token lives 7 days.
//     Any reload after a 15-minute gap — closing the laptop, the server
//     restarting — 401s on an expired access token while the refresh cookie is
//     still good. Spend it before concluding anything.
//  2. The server being unreachable is not a verdict on the user's identity.
//     A network error says nothing about whether they are signed in, so retry
//     instead of logging them out; a backend that comes back up finds the
//     session still there.
//
// Only a 401 that survives a refresh means genuinely signed out.
async function restoreSession() {
  const me = () => axios.get(`${API}/auth/me`, { withCredentials: true });
  for (let attempt = 0; ; attempt++) {
    try {
      const { data } = await me();
      return data;
    } catch (e) {
      if (e.response?.status === 401) {
        // Expired access token, valid refresh cookie: mint a new one and retry.
        await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
        const { data } = await me(); // throws if the refresh cookie is dead too
        return data;
      }
      // No response at all = server down/restarting. Wait for it, don't log out.
      if (!e.response && attempt < 3) {
        await sleep(600 * (attempt + 1));
        continue;
      }
      throw e;
    }
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      setUser(await restoreSession());
    } catch (e) {
      setUser(false);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      // Fetch full user with permissions + perm_scope
      const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(data);
      return { success: true };
    } catch (e) {
      const error = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      return { success: false, error };
    }
  };

  const register = async (email, password, full_name, role, unit, department) => {
    try {
      const { data } = await axios.post(`${API}/auth/register`, { email, password, full_name, role, unit, department }, { withCredentials: true });
      setUser(data);
      return { success: true };
    } catch (e) {
      const error = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      return { success: false, error };
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (e) {
      console.error('Logout error:', e);
    } finally {
      setUser(false);
    }
  };

  const hasPerm = (p) => user?.role === 'admin' || (user?.permissions || []).includes(p);
  const fullScope = user?.role === 'admin' || user?.perm_scope === 'all';

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    checkAuth,
    hasPerm,
    fullScope
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
